import {
    SdcCustomFormFieldType,
    LoadBalancedServiceType,
    SdTaskStatus,
    TaskType,
    FieldGroupType,
} from "@api/web-api-client";
import routesActions from "@routes/routesActions";

const SdConstants = {
    Shared: {
        ManageUsers: {
            InviteUserModal: {
                SearchDebounceMilliseconds: 750,
                ResetDebounceMilliseconds: 750,
            },
        },
        CustomDraftEditor: {
            OnChangeDebounceMilliseconds: 500,
        },
        CustomTusUpload: {
            DefaultMaxFileSizeBytes: 20971520,
        },
    },
    Home: {
        SearchDebounceMilliseconds: 250,
    },
    License: {
        ActivityThrottleMilliseconds: 15000,
        TrialLicenseName: "TRIAL",
        QueryStrings: {
            Licenses: "licenses",
            UsageHistory: "usage-history",
        },
    },
    Metrics: {
        ActivityThrottleMilliseconds: 60000,
    },
    Viewer: {
        KnownQueryParams: {
            TaskId: "taskId",
            ExternalId: "externalId",
            WorkspaceId: "workspaceId",
            TaskName: "taskName",
            TenantId: "tenantId",
            BatchName: "batchName",
            BatchId: "batchId",
            WorkspaceName: "workspaceName",
            NextIn: "nextIn",
            Timestamp: "timestamp",
        },
        Checkout: {
            MinimumRenewalIntervalMilliseconds: 240000, // 4 Minutes < than minimum of 5 minutes
            LastTaskIdStorageKey: "CheckoutLastTaskId",
        },
        Engines: {
            PdfMiner: "pdfminer",
            Tesseract: "Transym",
            FormRecognizer: "FormRecognizer",
            ContentUnderstanding: "ContentUnderstanding",
        },
        Controls: {
            ZoomAnimationDurationMilliseconds: 200,
            MaximumCopiedCharsDisplayLength: 50,
        },
        Quadtree: {
            MaximumObjects: 5000,
        },
        SidePanel: {
            JumpToNextShortcutDebounceMilliseconds: 250,
            UpdateGroupNameDebounceMilliseconds: 750,
            UpdateValueDebounceMilliseconds: 500,
            NotifyCustomFormChangeDebounceMilliseconds: 500,
            CustomFormChangeDebounceMilliseconds: 500,
            SaveSpinnerTimeoutMilliseconds: 1000,
            LastSelectionIdKey: "lastSelectionId",
            ReorderDebounceMilliseconds: 100,
            ValuesScrollOffset: -20,
            SummaryScrollOffset: -20,
            FormInputsExcludedFromZOffset: [
                SdcCustomFormFieldType.DatePicker,
                SdcCustomFormFieldType.DateTimePicker,
                SdcCustomFormFieldType.Picker,
                SdcCustomFormFieldType.PickerMultiple,
                SdcCustomFormFieldType.TimePicker,
            ],
        },
        Toolbar: {
            RedirectTimeoutSeconds: 5,
            Share: {
                UserSelectBackgroundColor: "#ffffff",
                UserSelectBorderColor: "rgba(0,17,255,0.32)",
            },
        },
        TemplateBuilder: {
            ControlsBottomMargin: 12,
            TableFeatures: {
                Icon: {
                    Path: "/assets/images/icons/table.svg",
                    DefaultOpacity: 0.8,
                    FocusOpacity: 1,
                    Scale: 0.3,
                    Anchor: [1.25, 0],
                    AnchorUnits: "fraction",
                },
                Table: {
                    DefaultColor: "rgba(60, 60, 60, 0)",
                    FocusColor: "rgba(60, 60, 60, 1)",
                    Width: 1,
                    Dash: [7],
                },
            },
            AutoLabelTableModal: {
                UnselectedCell: {
                    Color: "rgb(181, 178, 176)",
                    BackgroundColor: "rgb(250, 249, 248)",
                },
                SelectedCell: {
                    BackgroundColor: "rgba(0, 17, 255, 0.075)",
                },
            },
        },
        Anonymization: {
            ControlsBottomMargin: 12,
        },
        Layout: {
            Categories: {
                Table: "Categories.Layout.Table",
            },
        },
        Transcripts: {
            MaximumMediaSyncDelay: 0.1,
            NormalPlaybackRate: 1,
            PlaybackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
            UseAlternateEditorLayout: true,
            ShowParagraphEndTime: false,
            Timeline: {
                ProgressColor: "#0011ff5a",
                CursorColor: "#0011ff",
                WaveColor: "#82868b",
            },
            ChangeSpeakerDelayMilliseconds: 100,
            RegionSelections: {
                Timeout: 10,
                RegionId: "wavesurfer_",
                SyncLocalStorageItemKey: "waveRegionAdded",
            },
            Annotations: {
                DisplayAnnotations: true,
                DisplayAuthor: false,
                DisplayNewBadge: true,
                DisplayTimeInterval: true,
                DisplayDetails: false,
                AddIcon: "/assets/images/icons/annotationAdd.svg",
                SingleIcon: "/assets/images/icons/annotation.svg",
                MultipleIcon: "/assets/images/icons/annotations.svg",
            },
            TextualSummary: {
                GetSummaryIntervalMs: 20000,
            },
            ShareMediaSegment: {
                MinDaysExpiration: 1,
                DefaultDaysExpiration: 31,
                MaxDaysExpiration: 90,
                MaxDescriptionLength: 256,
            },
        },
    },
    User: {
        Id: "userId",
        QueryStrings: {
            Email: "email",
            Password: "password",
            ExternalLogins: "external-logins",
            Notifications: "notifications",
            TwoFactorAuthentication: "2fa",
        },
    },
    FullList: {
        MaxAssigneesToDisplay: 3,
        CustomColumns: "customColumns",
        SearchField: "search",
        TextSearchField: "textSearch",
        ActivityThrottleMilliseconds: 10000,
        ShowDropDownActionsButton: true,
        StatusWhenDropdownIsHidden: [
            SdTaskStatus.Deleted,
            SdTaskStatus.ProcessedAfterPeriodAndDeletedFiles,
        ],
        StatusWhenViewTaskShareHistory: [
            SdTaskStatus.Deleted,
            SdTaskStatus.ToReview,
            SdTaskStatus.Processed,
            SdTaskStatus.ProcessedAfterPeriodAndDeletedFiles,
        ],
        ShowDeleteSelection: false,
        CategoriesWithTypes: [
            "Categories.Receipts.ReceiptType",
            "Categories.IdDocuments.IdDocumentType",
        ],
        TaskTypesWhereIndexSearchIsSupported: [
            TaskType.Transcript,
            TaskType.Subtitles,
        ],
        QueryStrings: {
            ToReview: "to-review",
            MyAssignments: "my-assignments",
            Processed: "processed",
            Archive: "archive",
            TextSearch: "text-search",
        },
        CommonStatusInFilters: [
            {
                labelKey: "Enums.SdTaskStatus.Deleting",
                value: SdTaskStatus.Deleting,
            },
            {
                labelKey: "Enums.SdTaskStatus.QueuedForPreProcessing",
                value: SdTaskStatus.QueuedForPreProcessing,
            },
            {
                labelKey: "Enums.SdTaskStatus.QueuedForRecognizing",
                value: SdTaskStatus.QueuedForRecognizing,
            },
            {
                labelKey: "Enums.SdTaskStatus.QueuedForPostProcessing",
                value: SdTaskStatus.QueuedForPostProcessing,
            },
            {
                labelKey: "Enums.SdTaskStatus.QueuedForExport",
                value: SdTaskStatus.QueuedForExport,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingInProgress",
                value: SdTaskStatus.PreProcessingInProgress,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingFailed",
                value: SdTaskStatus.PreProcessingFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingDownloadFailed",
                value: SdTaskStatus.PreProcessingDownloadFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingSuccessful",
                value: SdTaskStatus.PreProcessingSuccessful,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingSplittingInProgress",
                value: SdTaskStatus.PreProcessingSplittingInProgress,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingSplittingFailed",
                value: SdTaskStatus.PreProcessingSplittingFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.RecognizingInProgress",
                value: SdTaskStatus.RecognizingInProgress,
            },
            {
                labelKey: "Enums.SdTaskStatus.RecognizingFailed",
                value: SdTaskStatus.RecognizingFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.RecognizingSuccessful",
                value: SdTaskStatus.RecognizingSuccessful,
            },
            {
                labelKey: "Enums.SdTaskStatus.PostProcessingInProgress",
                value: SdTaskStatus.PostProcessingInProgress,
            },
            {
                labelKey: "Enums.SdTaskStatus.PostProcessingFailed",
                value: SdTaskStatus.PostProcessingFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.PostProcessingSuccessful",
                value: SdTaskStatus.PostProcessingSuccessful,
            },
            {
                labelKey:
                    "Enums.SdTaskStatus.PostProcessingSharePointUploadingFailedAccessLock",

                value: SdTaskStatus.PostProcessingSharePointUploadingFailedAccessLock,
            },
            {
                labelKey: "Enums.SdTaskStatus.ExportInProgress",
                value: SdTaskStatus.ExportInProgress,
            },
            {
                labelKey: "Enums.SdTaskStatus.ExportFailed",
                value: SdTaskStatus.ExportFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.ExportSuccessful",
                value: SdTaskStatus.ExportSuccessful,
            },
            {
                labelKey: "Enums.SdTaskStatus.ValidationErrorInvalidDimensions",
                value: SdTaskStatus.ValidationErrorInvalidDimensions,
            },
            {
                labelKey:
                    "Enums.SdTaskStatus.GlobalGlossaryDownloadFailedError",

                value: SdTaskStatus.GlobalGlossaryDownloadFailedError,
            },
            {
                labelKey: "Enums.SdTaskStatus.GlossaryDownloadFailedError",
                value: SdTaskStatus.GlossaryDownloadFailedError,
            },
            {
                labelKey: "Enums.SdTaskStatus.GlossaryInvalidError",
                value: SdTaskStatus.GlossaryInvalidError,
            },
            {
                labelKey: "Enums.SdTaskStatus.GlobalGlossaryInvalidError",
                value: SdTaskStatus.GlobalGlossaryInvalidError,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingInsufficientVolume",
                value: SdTaskStatus.PreProcessingInsufficientVolume,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingConvertToPdfFailed",
                value: SdTaskStatus.PreProcessingConvertToPdfFailed,
            },
            {
                labelKey:
                    "Enums.SdTaskStatus.PreProcessingFileLengthCountFailed",
                value: SdTaskStatus.PreProcessingFileLengthCountFailed,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingUnknownLicenseError",
                value: SdTaskStatus.PreProcessingUnknownLicenseError,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingPDFReaderError",
                value: SdTaskStatus.PreProcessingPDFReaderError,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingInvalidLicenseError",
                value: SdTaskStatus.PreProcessingInvalidLicenseError,
            },
            {
                labelKey: "Enums.SdTaskStatus.PreProcessingInvalidPageRange",
                value: SdTaskStatus.PreProcessingInvalidPageRange,
            },
        ],
        MainListStatusInFilters: [
            {
                labelKey: "Enums.SdTaskStatus.InReview",
                value: SdTaskStatus.InReview,
            },
            {
                labelKey: "Enums.SdTaskStatus.ToReview",
                value: SdTaskStatus.ToReview,
            },
        ],
    },
    FullListHistory: {
        CustomColumns: "historyCustomColumns",
        HistStatusInFilters: [
            // Note: SdTaskStatus.ProcessedAfterPeriodAndDeletedFiles and SdTaskStatus.Deleted should not vsisible in hist
            {
                labelKey: "Enums.SdTaskStatus.Deleting",
                value: SdTaskStatus.Deleting,
            },
            {
                labelKey: "Enums.SdTaskStatus.Processed",
                value: SdTaskStatus.Processed,
            },
            {
                labelKey: "Enums.SdTaskStatus.SplitProcessed",
                value: SdTaskStatus.SplitProcessed,
            },
        ],
    },
    TemplateBuilder: {
        Training: {
            MinTasksForGoodTrain: 5,
            PoolingThrottleMilliseconds: 30000,
            FormRecognizerMaxPagesInTemplateTrainSet: 500,
            FormRecognizerMaxPagesInNeuralTrainSet: 50000,
            MinTrainingNeuralMinutes: 20,
            MaxTrainingNeuralHours: 1,
            MinTrainingTemplateMinutes: 1,
            MaxTrainingTemplateMinutes: 5,
        },
        SdKnownTemplateFieldTypes: {
            Table: "Table",
            KeyValues: "KeyValues",
        },
        LabellingStatusInFilters: [
            {
                labelKey: "Enums.SdTaskStatus.InReviewLabelling",
                value: SdTaskStatus.InReview,
            },
            {
                labelKey: "Enums.SdTaskStatus.ToReviewLabelling",
                value: SdTaskStatus.ToReview,
            },
        ],
    },
    Tasks: {
        // Value must be synced with backend Generic.TasksWithoutInSystemReview
        TasksWithoutInSystemReview: [TaskType.Translation],
        TasksWithShareMediaSegmentSupport: [
            TaskType.Transcript,
            TaskType.Subtitles,
        ],
        TasksWithoutShareSupport: [TaskType.Translation],
        Chatbot: {
            ChatbotName: "DocAI",
        },
        PageRange: {
            PageRangePatternRegex: RegExp(
                /^\s*([1-9]\d*)\s*(?:-\s*([1-9]\d*))?(?:\s*,\s*([1-9]\d*)\s*(?:-\s*([1-9]\d*))?)*\s*$/,
            ),
        },
    },
    Workspace: {
        LocalStorage: {
            LastSelectedWorkspaceIdsDict: "lastSelectedWorkspaceIdsDict",
        },
        Edit: {
            Fields: {
                CategoryKeyRegExp: /[^a-zA-Z\d.]/,
                ContentUnderstandingCategoryKeyRegExp: /(^[^A-Za-z])|\W/,
                ContentUnderstandingCategoryKeyMaxLength: 64,
                ContentUnderstandingFieldNameMaxLength: 64,
                ContentUnderstandingDescriptionMaxLength: 1024,
                TaskTypesWithoutFields: [
                    TaskType.Transcript,
                    TaskType.Subtitles,
                    TaskType.Translation,
                ],
                UpdateValueDebounceMilliseconds: 750,
                DefaultFillColor: "#0011ff1a",
                DefaultStrokeColor: "#0011ff",
            },
            TableHeaders: {
                CategoryKeyRegExp: /[^a-zA-Z\d]/,
            },
        },
        Import: {
            HideEmailImport: false,
            HideFolderMonitor: false,
            MinSplitChunkDurationMinutes: 1,
            DefaultSplitChunkDurationMinutes: 10,
            MinSplitChunkNumberPages: 1,
            DefaultSplitChunkNumberPages: 10,
            PageRangePlaceholder: "1,3-5",
            MaxPages: 2000,
            MaxPagesContentUnderstanding: 300,
            MaxTusChunkSizeBytes: 95 * 1024 * 1024, // 95mb, lower than Cloudflare limit for request bodies in the Free/Pro tier https://community.cloudflare.com/t/413-request-entity-too-large-how-to-increase-the-size/330117
            TaskTypesThatCannotBeSplit: [
                TaskType.ImageAnonymization,
                TaskType.LegalDocument,
                TaskType.Translation,
            ],
            Email: {
                UploadStorageExpiration: 31,
            },
        },
        AdvancedSettings: {
            MinDaysUntilHistoryRemoval: 1,
            DefaultDaysUntilHistoryRemoval: 31,
            MaxDaysUntilHistoryRemoval: 90,
            MinHoursUntilTasksConsideredStuck: 1,
            DefaultHoursUntilTasksConsideredStuck: 1,
        },
        Export: {
            MinExportFileToUrlExpireHours: 1,
            MaxExportFileToUrlExpireHours: 168,
        },
        Rules: {
            CustomValidationName: "field-rule-validation",
            Compare: {
                ColSum: {
                    FieldToCompare: {
                        AllowedGroupTypes: [FieldGroupType.Table],
                    },
                    CompareWithField: {
                        AllowedGroupTypes: [FieldGroupType.QrCodePt, undefined],
                    },
                    AllowedFieldTypes: [
                        SdcCustomFormFieldType.Decimal,
                        SdcCustomFormFieldType.Currency,
                        SdcCustomFormFieldType.Numeric,
                    ],
                },
                Other: {
                    FieldToCompare: {
                        AllowedGroupTypes: [FieldGroupType.QrCodePt, undefined],
                    },
                    CompareWithField: {
                        AllowedGroupTypes: [FieldGroupType.QrCodePt, undefined],
                    },
                    AllowedFieldTypes: [
                        undefined,
                        SdcCustomFormFieldType.Entry,
                        SdcCustomFormFieldType.EntryArea,
                        SdcCustomFormFieldType.DatePicker,
                        SdcCustomFormFieldType.Decimal,
                        SdcCustomFormFieldType.Currency,
                        SdcCustomFormFieldType.Numeric,
                    ],
                },
            },
        },
        QueryStrings: {
            Basic: "basic",
            Users: "users",
            DefaultAssignment: "default-assignment",
            Fields: "fields",
            Rules: "rules",
            PredefinedText: "predefined-texts",
            PredefinedSpeakers: "predefined-speakers",
            TranslationStorage: "translation-storage",
            Storage: "storage",
            Export: "export",
            AdvancedSettings: "advanced-settings",
            Notifications: "notifications",
            ForDevelopers: "for-developers",
        },
        ForDevelopers: {
            GetHookServiceType: (taskType: TaskType) => {
                switch (taskType) {
                    case TaskType.Invoice:
                    case TaskType.Receipt:
                    case TaskType.Labelling:
                    case TaskType.Custom:
                    case TaskType.IdDocument:
                    case TaskType.Layout:
                        return LoadBalancedServiceType.FormRecognizerHook;
                    default:
                        return undefined;
                }
            },
        },
        PageRange: {
            IsAllowedWorkspaceForPageRange: [
                TaskType.Invoice,
                TaskType.Receipt,
                TaskType.Custom,
                TaskType.Layout,
                TaskType.IdDocument,
                TaskType.DocumentContentUnderstanding,
            ],
        },
    },
    Template: {
        QueryStrings: {
            Basic: "basic",
            ForDevelopers: "for-developers",
        },
    },
    Tenant: {
        QueryStrings: {
            Basic: "basic",
            Users: "users",
            Secrets: "secrets",
            Connectors: "connectors",
        },
    },
    Localization: {
        CultureLangMap: {
            pt: "pt-PT",
            en: "en-US",
            de: "de-DE",
            fr: "fr-FR",
            es: "es-ES",
        },
        ReverseCultureLangMap: {
            "pt-PT": "pt",
            "en-US": "en",
            "de-DE": "de",
            "fr-FR": "fr",
            "es-ES": "es",
        },
    },
    KnownFieldTypes: {
        Table: "TABLE",
        QrCode: "QRCODE",
        String: "STRING",
        TemplateBuilderField: "TEMPLATE_BUILDER_FIELD",
        TemplateBuilderTableField: "TEMPLATE_BUILDER_TABLE_FIELD",
    },
    KnownCategories: {
        TemplateBuilderField: "Categories.LabellingTemplate.Field",
    },
    KnownFields: {
        IsDetectedFields: "IsDetectedFields",
        AvgConfidence: "AvgConfidence",
        SmallestConfidence: "SmallestConfidence",
        HasEmptyRequiredFields: "HasEmptyRequiredFields",
        HasFieldsNotMatchingRegex: "HasFieldsNotMatchingRegex",
        HasFieldsNotMatchingComparison: "HasFieldsNotMatchingComparison",
        HasFieldsNotMatchingRules: "HasFieldsNotMatchingRules",
        QRCodeInvalidFormat: "QRCodeInvalidFormat",
        IsVideo: "IsVideo",
        MediaDurationInSeconds: "MediaDurationInSeconds",
        TotalCharacterCount: "TotalCharacterCount",
        HasBeenEdited: "HasBeenEdited",
    },
    Sidebar: {
        ClickToOpenRoutes: [routesActions.Task.Viewer.path],
    },
    Flags: {
        Viewer: {
            ShowAddedBadge: false,
            ShowEditedBadge: true,
            ShowMultipleChildrenAnonymization: true,
        },
    },
    PromptActions: {
        Pop: "POP",
        Replace: "REPLACE",
    },
    QueryStringKeys: {
        Tab: "tab",
        Culture: "culture",
    },
    Hosting: {
        HostDockerInternal: "host.docker.internal",
        Localhost: "localhost",
    },
    Colors: {
        PrimaryColor: "#0011ff",
        SecundaryColor: "#82868b",
        Chatbot: {
            ChatbotMessageFromUserColor: "#4e8af2",
        },
    },
};

export const enum SdTaskListCustomActionsType {
    View = "view",
    FinishReview = "finishReview",
    Reprocess = "reprocess",
    Delete = "delete",
    DevDashboard = "devdashboard",
    Export = "export-excel",
    ExportCsv = "export-csv",
    ExportJson = "export-json",
    ExportXml = "export-xml",
    EventInspector = "event-inspector",
    Recover = "recover",
    EditAssignments = "edit-assignments",
    IndexLogs = "index-logs",
    ViewTaskShareHistory = "view-task-share-history",
}

export default SdConstants;
